import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, LinearProgress,
  Radio, RadioGroup, FormControlLabel, FormControl, Alert,
  Chip, Divider, CircularProgress,
} from '@mui/material';
import {
  Timer, CheckCircle, Cancel, EmojiEvents, ArrowBack, ArrowForward,
} from '@mui/icons-material';
import { CourseTest, MCQQuestion, TestAttemptResult } from '../../types';
import { learnerAPI } from '../../services/api';

interface Props {
  courseId: string;
  test: CourseTest;
  onFinish: (result: TestAttemptResult) => void;
  onCancel: () => void;
}

type Phase = 'confirm' | 'playing' | 'results';

const TestPlayer: React.FC<Props> = ({ courseId, test, onFinish, onCancel }) => {
  const [phase, setPhase] = useState<Phase>('confirm');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [timeLeft, setTimeLeft] = useState(test.time_limit * 60);
  const [startTime, setStartTime] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<TestAttemptResult | null>(null);

  const submit = useCallback(async (forceAnswers?: Record<string, number>) => {
    setSubmitting(true);
    const used = forceAnswers ?? answers;
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    try {
      const payload = {
        answers: test.questions.map(q => ({
          question_id: q.id,
          answer: used[q.id] ?? -1,
        })),
        time_spent: timeSpent,
      };
      const res = await learnerAPI.submitTestAttempt(courseId, test.type, payload);
      const r = res.data as TestAttemptResult;
      setResult(r);
      setPhase('results');
      onFinish(r);
    } catch (e) {
      console.error('Submit error', e);
    } finally {
      setSubmitting(false);
    }
  }, [answers, courseId, onFinish, startTime, test.questions, test.type]);

  // Countdown timer
  useEffect(() => {
    if (phase !== 'playing') return;
    if (timeLeft <= 0) {
      submit();
      return;
    }
    const t = setTimeout(() => setTimeLeft(p => p - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, timeLeft, submit]);

  const startTest = () => {
    setStartTime(Date.now());
    setPhase('playing');
  };

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const answered = Object.keys(answers).length;
  const total = test.questions.length;
  const q: MCQQuestion | undefined = test.questions[currentQ];
  const timerColor = timeLeft < 60 ? 'error' : timeLeft < 300 ? 'warning' : 'primary';

  // ── Confirm Screen ────────────────────────────────────────────────────────
  if (phase === 'confirm') {
    const isFinal = test.type === 'final';
    return (
      <Box maxWidth={600} mx="auto" py={4}>
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>{test.title}</Typography>
            <Chip
              label={isFinal ? 'Final Exam' : 'Practice Quiz'}
              color={isFinal ? 'success' : 'warning'}
              sx={{ mb: 2 }}
            />
            <Typography color="text.secondary" paragraph>{test.description}</Typography>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" gap={3} flexWrap="wrap" mb={3}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={700} color="primary">{total}</Typography>
                <Typography variant="caption" color="text.secondary">Questions</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={700} color="warning.main">{test.time_limit}</Typography>
                <Typography variant="caption" color="text.secondary">Minutes</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={700} color="success.main">{test.passing_score}%</Typography>
                <Typography variant="caption" color="text.secondary">To Pass</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={700} color="error.main">
                  {test.max_attempts === -1 ? '∞' : test.max_attempts}
                </Typography>
                <Typography variant="caption" color="text.secondary">Attempts</Typography>
              </Box>
            </Box>
            {isFinal && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This is a one-attempt certification exam. Make sure you've reviewed all course material before starting.
              </Alert>
            )}
            <Box display="flex" gap={2}>
              <Button variant="outlined" onClick={onCancel} fullWidth>Back to Course</Button>
              <Button variant="contained" color={isFinal ? 'success' : 'primary'} onClick={startTest} fullWidth size="large">
                Start {isFinal ? 'Exam' : 'Quiz'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Results Screen ────────────────────────────────────────────────────────
  if (phase === 'results' && result) {
    return (
      <Box maxWidth={700} mx="auto" py={4}>
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Box textAlign="center" mb={3}>
              {result.passed ? (
                <>
                  <EmojiEvents sx={{ fontSize: 72, color: 'success.main' }} />
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {test.type === 'final' ? 'Congratulations! You Passed!' : 'Great Job!'}
                  </Typography>
                </>
              ) : (
                <>
                  <Cancel sx={{ fontSize: 72, color: 'error.main' }} />
                  <Typography variant="h4" fontWeight={700} color="error.main">Not Passed</Typography>
                </>
              )}
            </Box>

            <Box display="flex" justifyContent="center" gap={4} mb={3} flexWrap="wrap">
              <Box textAlign="center">
                <Typography variant="h3" fontWeight={700} color={result.passed ? 'success.main' : 'error.main'}>
                  {Math.round(result.percentage)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">Score</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h3" fontWeight={700}>{result.score}/{result.total_points}</Typography>
                <Typography variant="caption" color="text.secondary">Points</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h3" fontWeight={700}>{formatTime(result.time_spent)}</Typography>
                <Typography variant="caption" color="text.secondary">Time Taken</Typography>
              </Box>
            </Box>

            <LinearProgress
              variant="determinate"
              value={result.percentage}
              color={result.passed ? 'success' : 'error'}
              sx={{ height: 10, borderRadius: 5, mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mb={3}>
              Required: {result.passing_score}%
            </Typography>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Question Review</Typography>

            {test.questions.map((q, i) => {
              const qResult = result.results?.find(r => r.question_id === q.id);
              const correct = qResult?.correct ?? false;
              return (
                <Card
                  key={q.id}
                  variant="outlined"
                  sx={{ mb: 2, borderColor: correct ? 'success.main' : 'error.main', borderWidth: 2 }}
                >
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                      {correct
                        ? <CheckCircle color="success" fontSize="small" sx={{ mt: 0.3 }} />
                        : <Cancel color="error" fontSize="small" sx={{ mt: 0.3 }} />}
                      <Typography variant="body2" fontWeight={500}>Q{i + 1}. {q.question_text}</Typography>
                    </Box>
                    {q.options.map((opt, oi) => (
                      <Typography
                        key={oi}
                        variant="body2"
                        sx={{
                          pl: 3,
                          py: 0.25,
                          color: oi === qResult?.correct_answer ? 'success.main' : 'text.secondary',
                          fontWeight: oi === qResult?.correct_answer ? 700 : 400,
                        }}
                      >
                        {String.fromCharCode(65 + oi)}. {opt}
                        {oi === qResult?.correct_answer && ' ✓'}
                      </Typography>
                    ))}
                    {qResult?.explanation && (
                      <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                        <Typography variant="caption">{qResult.explanation}</Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {test.type === 'final' && result.passed && (
              <Alert severity="success" icon={<EmojiEvents />} sx={{ mt: 2 }}>
                Your certificate will be available on your dashboard. Well done!
              </Alert>
            )}

            <Box mt={3}>
              <Button variant="contained" onClick={onCancel} fullWidth>Back to Course</Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Playing Screen ────────────────────────────────────────────────────────
  if (!q) return null;

  return (
    <Box maxWidth={700} mx="auto" py={4}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle2" color="text.secondary">
          {test.title} · Question {currentQ + 1} of {total}
        </Typography>
        <Chip
          icon={<Timer />}
          label={formatTime(timeLeft)}
          color={timerColor}
          variant="outlined"
          sx={{ fontFamily: 'monospace', fontWeight: 700 }}
        />
      </Box>

      {/* Progress */}
      <LinearProgress
        variant="determinate"
        value={((currentQ + 1) / total) * 100}
        sx={{ mb: 3, height: 6, borderRadius: 3 }}
      />

      {/* Question Card */}
      <Card elevation={2}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>{q.question_text}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={3}>
            {q.points} point{q.points !== 1 ? 's' : ''}
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={answers[q.id] !== undefined ? String(answers[q.id]) : ''}
              onChange={e => setAnswers(prev => ({ ...prev, [q.id]: parseInt(e.target.value) }))}
            >
              {q.options.map((opt, i) => (
                <FormControlLabel
                  key={i}
                  value={String(i)}
                  control={<Radio />}
                  label={`${String.fromCharCode(65 + i)}. ${opt}`}
                  sx={{
                    border: '1px solid',
                    borderColor: answers[q.id] === i ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    mb: 1,
                    px: 1,
                    ml: 0,
                    width: '100%',
                    bgcolor: answers[q.id] === i ? 'primary.50' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Navigation */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => setCurrentQ(p => p - 1)}
          disabled={currentQ === 0}
        >
          Previous
        </Button>

        <Typography variant="caption" color="text.secondary">
          {answered}/{total} answered
        </Typography>

        {currentQ < total - 1 ? (
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={() => setCurrentQ(p => p + 1)}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            onClick={() => submit()}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
          </Button>
        )}
      </Box>

      {/* Question Navigator */}
      <Box display="flex" flexWrap="wrap" gap={0.5} mt={3} justifyContent="center">
        {test.questions.map((_, i) => (
          <Button
            key={i}
            size="small"
            variant={currentQ === i ? 'contained' : answers[test.questions[i].id] !== undefined ? 'outlined' : 'text'}
            color={currentQ === i ? 'primary' : 'inherit'}
            onClick={() => setCurrentQ(i)}
            sx={{ minWidth: 36, p: 0.5 }}
          >
            {i + 1}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default TestPlayer;
